import { useId, useState, type ReactNode } from 'react';
import { Files, ScanText, Table2, X } from 'lucide-react';
import type { LearningEligibility, TrainingReadinessItem } from '../shared/desktop-api';
import './training-readiness.css';

function ReadinessCategory({
  category,
  label,
  Icon,
  items,
}: {
  category: string;
  label: string;
  Icon: typeof Files;
  items: TrainingReadinessItem[];
}) {
  const id = useId();
  const [position, setPosition] = useState({ left: 0, top: 0 });
  const ready = items.filter((item) => item.ready);
  return (
    <div className="training-category">
      <button
        type="button"
        className={`learning-category learning-category-${category}`}
        popoverTarget={id}
        onClick={(event) => {
          const box = event.currentTarget.getBoundingClientRect();
          setPosition({
            left: Math.max(12, Math.min(box.left, window.innerWidth - 392)),
            top: Math.max(12, Math.min(box.bottom + 8, window.innerHeight - 444)),
          });
        }}
      >
        <Icon size={23} aria-hidden="true" />
        <strong>{label}</strong>
        <span className="learning-category-count">
          <b>{ready.length}</b> of {items.length} ready
        </span>
        <small>{ready.length === items.length ? 'View items' : 'See what’s needed'}</small>
      </button>
      <div
        id={id}
        popover="auto"
        className="training-readiness-popover"
        style={position}
        role="dialog"
        data-telemetry-dialog="none"
        aria-label={`${label} ready for training`}
      >
        <header>
          <strong>{label}</strong>
          <button
            type="button"
            className="icon-button"
            popoverTarget={id}
            popoverTargetAction="hide"
            aria-label="Close readiness details"
          >
            <X size={18} />
          </button>
        </header>
        <div className="training-readiness-list">
          {[true, false].map((isReady) => (
            <section key={String(isReady)}>
              <h4>
                {isReady ? 'Ready' : 'Not ready'}{' '}
                <span>{items.filter((item) => item.ready === isReady).length}</span>
              </h4>
              {items
                .filter((item) => item.ready === isReady)
                .map((item) => (
                  <div className="training-readiness-item" key={item.id}>
                    <strong>{item.name}</strong>
                    {!isReady && item.needs.map((need) => <p key={need}>{need}</p>)}
                  </div>
                ))}
              {!items.some((item) => item.ready === isReady) && (
                <p className="training-readiness-empty">
                  {isReady ? 'No items ready yet.' : 'All items have enough examples.'}
                </p>
              )}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

export function LearningReadiness({
  evidence,
  loading,
  failed,
  setupUnfinished = false,
  children,
}: {
  evidence: LearningEligibility | null;
  loading: boolean;
  failed: boolean;
  setupUnfinished?: boolean;
  children?: ReactNode;
}) {
  const items = evidence?.readinessItems;
  const available = !loading && !failed && !setupUnfinished && items != null;
  const ready = items?.filter((item) => item.ready).length ?? 0;
  return (
    <section className="learning-readiness" aria-label="Ready for training">
      <header>
        <div className="learning-readiness-title">
          <div>
            <strong>Ready for training</strong>
            <span>
              {loading
                ? 'Checking confirmed examples…'
                : failed
                  ? 'Could not load training readiness. Close and reopen Learning to try again.'
                  : setupUnfinished
                    ? 'Save this job’s setup before training.'
                    : !items
                      ? 'Training readiness is unavailable.'
                      : `${ready} of ${items.length} items have enough confirmed examples for training.`}
            </span>
          </div>
        </div>
      </header>
      {available && (
        <div className="learning-readiness-categories">
          {[
            { category: 'types', label: 'Document types', Icon: Files },
            { category: 'fields', label: 'Fields', Icon: ScanText },
            { category: 'tables', label: 'Line items', Icon: Table2 },
          ].map((group) => {
            const members = items.filter((item) => item.category === group.category);
            return members.length ? (
              <ReadinessCategory key={group.category} {...group} items={members} />
            ) : null;
          })}
        </div>
      )}
      {children}
    </section>
  );
}
